<template>
  <div class="main-content">
    <div class="section__content section__content--p30">
      <div class="container-fluid create-job-container p-b-40">
        <v-form v-model="isFormValid">
          <v-card class="p-b-30 p-t-30 p-l-20">
            <div class="el-loading-mask" v-if="loading">
              <div class="el-loading-spinner">
                <svg viewBox="25 25 50 50" class="circular">
                  <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
                </svg>
              </div>
            </div>
            <!-- Card Title and Priority -->
            <div class="row">
              <div class="btn-actions-pane-left">
                <h3 class="title-1 p-l-185">
                  {{ text('new_file_transcoding') }}
                </h3>
              </div>
              <div class="btn-actions-pane-right p-r-45">
                <div class="selection-control">
                  <v-checkbox
                    hide-details
                    v-model="form.urgentTask"/>
                  <span class="custom-selector-label p-l-15 priority"
                        :class="{urgent: form.urgentTask}"
                        @click="form.urgentTask = !form.urgentTask">
                    {{ text('high_priority') }}
                  </span>
                </div>
              </div>
            </div>
            <!-- Name and Profile -->
            <div class="section">
              <div class="row p-b-10">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-10 section-description">
                  {{ text('new_file_transcoding_name_text') }}
                </div>
              </div>
              <div class="row">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-4">
                  <v-text-field
                    v-model="form.name"
                    :label="text('title_label')"
                    :counter="255"
                    :rules="rules.nameRules"
                    required>
                  </v-text-field>
                </div>
                <div class="col-md-6 p-r-0">
                  <v-select
                    prepend-icon="fas fa-sliders-h"
                    v-model="form.profile"
                    :label="text('profile_label')"
                    item-text="name"
                    item-value="id"
                    :items="profiles">
                  </v-select>
                </div>
              </div>
              <div class="row p-b-10">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-10 body-1">
                  <a href="#" @click.prevent="copyDialog = true">
                    {{ text('copy_btn') }}
                  </a>
                </div>
              </div>
            </div>
            <!-- INPUT -->
            <div class="section">
              <div class="row p-b-10">
                <div class="col-md-2 col-l">
                  <h2>{{ text('input') }}</h2>
                </div>
                <div class="col-md-10 section-description">
                  {{ text('input_url_text') }}
                </div>
              </div>
              <div class="row">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-10 p-r-0">
                  <v-text-field
                    outline
                    v-model="form.input.url.url"
                    @change="onInputURLChange"
                    :label="text('input_label')"
                    :counter="1024"
                    :placeholder="form.input.url.selectedType + text('host_path_hint')"
                    :append-outer-icon="form.input.url.showDetails ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                    @click:append-outer="form.input.url.showDetails = !form.input.url.showDetails"
                    :error="form.input.url.url !== '' && !form.input.url.hostPath">
                  </v-text-field>
                  <v-expand-transition>
                    <div class="url-details p-l-10" v-show="form.input.url.showDetails">
                      <v-radio-group v-model="form.input.url.selectedType" row height="3" hide-details>
                        <v-radio :value="'http'" @change="changeInputType('http')"></v-radio>
                        <div class="custom-selector-label" @click="changeInputType('http')">HTTP</div>
                        <v-radio :value="'ftp'" @change="changeInputType('ftp')"></v-radio>
                        <div class="custom-selector-label" @click="changeInputType('ftp')">FTP</div>
                        <v-radio :value="'aspera'" @change="changeInputType('aspera')"></v-radio>
                        <div class="custom-selector-label" @click="changeInputType('aspera')">Aspera</div>
                        <v-radio :value="'azure'" @change="changeInputType('azure')"></v-radio>
                        <div class="custom-selector-label" @click="changeInputType('azure')">Azure</div>
                      </v-radio-group>
                      <div class="row p-t-10">
                        <div class="col-md-5">
                          <v-text-field
                            hide-details
                            @change="buildInputURL()"
                            v-model="form.input.url.login"
                            :label="text('login')"
                          >
                          </v-text-field>
                        </div>
                        <div class="col-md-7">
                          <v-text-field
                            hide-details
                            @change="buildInputURL()"
                            v-model="form.input.url.password"
                            :label="form.input.url.selectedType === 'azure' ? text('shared_key') : text('password')">
                          </v-text-field>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-md-12">
                          <v-text-field
                            hide-details
                            @change="buildInputURL()"
                            v-model="form.input.url.hostPath"
                            :label="text('host_path')"></v-text-field>
                        </div>
                      </div>
                      <div class="row p-t-20" v-show="['http', 'ftp'].includes(form.input.url.selectedType)">
                        <div class="col-md-12 secure-setting">
                          <v-checkbox hide-details
                                      @change="buildInputURL()"
                                      v-model="form.input.url.secure"></v-checkbox>
                          <span @click="changeInputSecure"
                                class="custom-selector-label p-l-20">
                            {{ text('secure') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </v-expand-transition>
                </div>
              </div>
            </div>
            <!-- OUTPUTS -->
            <div class="section">
              <div class="row p-b-5">
                <div class="col-md-2 col-l">
                  <h2>{{ text('output_header') }}</h2>
                </div>
                <div class="col-md-10 section-description">
                  <!--Add one or more output <span @click="formatInfoDialog=true">formats</span> and specify the path(s) to the server folder to upload the result to.-->
                  {{ text('file_job_output_text') }}
                </div>
              </div>
              <div class="row" v-for="(output, i) of form.outputs"
                   :key="i"
                   :class="{'p-t-20': i > 0, 'p-b-20': form.outputs.length > 1 && i < form.outputs.length-1}">
                <div class="col-md-2 col-l">
                  <p class="p-t-15">
                    #{{ i+1 }} {{ text('of') }} {{ form.outputs.length }}
                  </p>
                  <p v-if="form.outputs.length > 1">
                    <i class="zmdi zmdi-minus remove-icon p-r-5"></i>
                    <a class="remove-output" @click="removeOutput(i)">
                      {{ text('remove_btn') }}
                    </a>
                  </p>
                </div>
                <div class="col-md-10 p-r-0">
                  <v-tabs style="min-height: 210px;">
                    <v-tab>{{ text('general_tab') }}</v-tab>
                    <v-tab>{{ text('subtitles_tab') }}</v-tab>
                    <v-tab>{{ text('drm_tab') }}</v-tab>
                    <v-tab>{{ text('compress_tab') }}</v-tab>
                    <!-- General tab -->
                    <v-tab-item>
                      <div class="row p-t-20">
                        <div class="col-md-4" style="max-width: 200px;">
                          <v-select
                            v-model="output.format"
                            :label="text('format_label')"
                            item-text="title"
                            item-value="extId"
                            @change="onFormatChange(i)"
                            :items="formats">
                          </v-select>
                        </div>
                        <div class="col-md-4" style="max-width: 200px;" v-if="output.format === 'segmented_hls'">
                          <v-select
                            :items="hlsVersions"
                            :label="text('version_label')"
                            v-model="output.hlsVersion"></v-select>
                        </div>
                        <div class="col-md-4" style="max-width: 200px;" v-if="['segmented_hls', 'segmented_dash', 'segmented_ss'].includes(output.format)">
                          <v-text-field
                            type="number"
                            :label="text('segment_size_label')"
                            v-model="output.segmentSize"
                            :rules="rules.segmentSize"
                            :suffix="text('sec')">
                          </v-text-field>
                        </div>
                      </div>
                      <div class="row p-t-10">
                        <div class="col-md-12 p-r-0" v-for="(url, j) of output.urls" :key="j">
                          <v-text-field
                            outline
                            v-model="url.url"
                            @change="onOutputURLChange(i, j)"
                            :placeholder="url.selectedType + text('host_path_hint')"
                            :label="outputLabel(i, j)"
                            :counter="1024"
                            :append-icon="url.showDetails ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                            @click:append="url.showDetails = !url.showDetails"
                            :error="url.url !== '' && !url.hostPath && url.selectedType !== 'origin'"
                            @click:append-outer="addOutputURL(i)">
                            <template v-slot:append-outer>
                              <v-btn
                                text icon
                                class="m-0"
                                @click.stop="removeOutputURL(i, j)"
                                :disabled="output.urls.length === 1">
                                <i class="zmdi zmdi-delete"></i>
                              </v-btn>
                              <v-btn
                                text icon
                                class="m-0"
                                @click.stop="addOutputURL(i)"
                                :disabled="j < output.urls.length-1">
                                <i class="zmdi zmdi-plus"></i>
                              </v-btn>
                            </template>
                          </v-text-field>
                          <v-expand-transition>
                            <div class="url-details p-l-10" v-show="url.showDetails">
                              <v-radio-group v-model="url.selectedType" row height="3" hide-details>
                                <v-radio :value="'ftp'" @change="changeOutputType(i, j, 'ftp')"></v-radio>
                                <div class="custom-selector-label" @click="changeOutputType(i, j, 'ftp')">FTP</div>
                                <v-radio :value="'aspera'" @change="changeOutputType(i, j, 'aspera')"></v-radio>
                                <div class="custom-selector-label" @click="changeOutputType(i, j, 'aspera')">Aspera</div>
                                <v-radio :value="'azure'" @change="changeOutputType(i, j, 'azure')"></v-radio>
                                <div class="custom-selector-label" @click="changeOutputType(i, j, 'azure')">Azure</div>
                                <v-radio :value="'origin'" @change="changeOutputType(i, j, 'origin')"></v-radio>
                                <div class="custom-selector-label" @click="changeOutputType(i, j, 'origin')">Origin</div>
                              </v-radio-group>
                              <div class="row p-t-10">
                                <div class="col-md-5">
                                  <v-text-field
                                    hide-details
                                    @change="buildOutputUrl(i, j)"
                                    v-model="url.login"
                                    :label="text('login')"
                                  >
                                  </v-text-field>
                                </div>
                                <div class="col-md-7 p-r-95">
                                  <v-text-field
                                    hide-details
                                    @change="buildOutputUrl(i, j)"
                                    v-model="url.password"
                                    :label="url.selectedType === 'azure' ? text('shared_key') : text('password')">
                                  </v-text-field>
                                </div>
                              </div>
                              <div class="row p-b-10"
                                   :class="{'p-b-30': output.urls.length > 1 && j < output.urls.length-1 && url.selectedType !== 'ftp'}">
                                <div class="col-md-12 p-r-95">
                                  <v-text-field
                                    hide-details
                                    @change="buildOutputUrl(i, j)"
                                    v-model="url.hostPath"
                                    :label="text('host_path')">
                                  </v-text-field>
                                </div>
                              </div>
                              <div class="row p-b-20"
                                   v-show="['http', 'ftp'].includes(url.selectedType)"
                                   :class="{'p-b-30': output.urls.length > 1 && j < output.urls.length-1}">
                                <div class="col-md-12 secure-setting">
                                  <v-checkbox
                                    hide-details
                                    @change="buildOutputUrl(i, j)"
                                    v-model="url.secure"></v-checkbox>
                                  <span @click="changeOutputSecure(i, j)"
                                        class="custom-selector-label p-l-20">
                                    {{ text('secure') }}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </v-expand-transition>
                        </div>
                      </div>
                    </v-tab-item>
                    <!-- Subtitles tab -->
                    <v-tab-item>
                      <div class="row p-t-20">
                        <div class="col-md-12 p-r-0">
                          <p class="tab-description" v-if="!(output.format === 'segmented_hls' && output.hlsVersion === 5)">
                            {{ text('job_sub_text') }}
                          </p>
                          <div v-else>
                            <p class="tab-description m-b-10">
                              {{ text('job_sub_text2') }}
                            </p>
                            <table v-for="(subtitle, subIndex) of output.subtitles" :key="subIndex">
                            <tr>
                              <td class="p-r-10">
                                <v-text-field
                                  label="WebVTT URL"
                                  @change="onSubtitleChange(i, subIndex)"
                                  v-model.trim="subtitle.url"
                                  :error="subtitle.url !== '' && !subtitle.hostPath"
                                  placeholder="scheme://host/subtitles.vtt"
                                  clearable></v-text-field>
                              </td>
                              <td class="p-r-10" style="width: 15%">
                                <v-autocomplete
                                  clearable
                                  :label="text('lang_label')"
                                  item-text="title"
                                  item-value="code"
                                  v-model="subtitle.code"
                                  :items="langCodes"></v-autocomplete>
                              </td>
                              <td style="width: 15%">
                                <v-text-field
                                  clearable
                                  :label="text('display_title_label')"
                                  v-model="subtitle.title"></v-text-field>
                              </td>
                              <td style="width: 10%">
                                <v-btn
                                  text icon
                                  class="m-0"
                                  :disabled="output.subtitles.length === 1"
                                  @click.stop="removeSubtitles(i, subIndex)">
                                  <i class="zmdi zmdi-delete"></i>
                                </v-btn>
                                <v-btn
                                  text icon
                                  class="m-0"
                                  @click.stop="addSubtitles(i)"
                                  :disabled="subIndex < output.subtitles.length-1">
                                  <i class="zmdi zmdi-plus"></i>
                                </v-btn>
                              </td>
                            </tr>
                          </table>
                          </div>
                        </div>
                      </div>
                    </v-tab-item>
                    <!-- DRM tab -->
                    <v-tab-item>
                      <div class="row p-t-20">
                        <div class="col-md-12 p-r-15">
                          <p class="tab-description p-b-10" v-html="text('job_drm_text')"></p>
                          <v-radio-group v-model="output.drm" row class="p-0" height="3" hide-details>
                            <v-radio
                              :value="'none'"
                              @change="setDRM(i, 'none')"/>
                            <div class="custom-selector-label" @click="setDRM(i, 'none')">
                              none
                            </div>
                            <template v-if="output.format === 'segmented_dash'">
                              <v-radio :value="'widevine'" @change="setDRM(i, 'widevine')"/>
                              <div class="custom-selector-label" @click="setDRM(i, 'widevine')">
                                Widevine
                              </div>
                            </template>
                            <template v-if="['segmented_dash', 'segmented_ss'].includes(output.format)">
                              <v-radio :value="'playready'" @change="setDRM(i, 'playready')"/>
                              <div class="custom-selector-label" @click="setDRM(i, 'playready')">
                                PlayReady
                              </div>
                            </template>
                          </v-radio-group>
                          <!-- Widevine Fields -->
                          <v-expand-transition>
                            <div v-show="output.drm === 'widevine'" class="p-t-20">
                              <div class="row">
                                <div class="col-md-12">
                                  <v-text-field
                                    v-model.trim="output.widevine.licenseServer"
                                    :counter="1024"
                                    :rules="rules.drmServerURL"
                                    label="*Key Server URL"
                                    hint="e.g. https://my-license-server/cenc/"
                                    clearable></v-text-field>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-md-12">
                                  <v-text-field
                                    v-model.trim="output.widevine.provider"
                                    :counter="512"
                                    :rules="rules.wvProvider"
                                    hint="e.g. my-provider"
                                    label="*Provider"
                                    clearable></v-text-field>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-md-7">
                                  <v-text-field
                                    v-model="output.widevine.key"
                                    :counter="64"
                                    :rules="rules.wvKey"
                                    label="*AES 128-bit signing key (64 characters)"
                                    clearable></v-text-field>
                                </div>
                                <div class="col-md-5 p-l-20">
                                  <v-text-field
                                    v-model="output.widevine.iv"
                                    :counter="32"
                                    :rules="rules.wvIV"
                                    label="*AES signing IV (32 characters)"
                                    clearable></v-text-field>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-md-12">
                                  <v-text-field
                                    v-model="output.widevine.packageName"
                                    :counter="128"
                                    :rules="rules.wvPackageName"
                                    hint="e.g. output_1080p"
                                    label="*Package Name"
                                    clearable></v-text-field>
                                </div>
                              </div>
                            </div>
                          </v-expand-transition>
                          <!-- PlayReady Fields -->
                          <v-expand-transition>
                            <div v-show="output.drm === 'playready'" class="p-t-20">
                              <div class="row">
                                <div class="col-md-12">
                                  <v-text-field
                                    v-model.trim="output.playready.licenseServerURL"
                                    :counter="1024"
                                    :rules="rules.drmServerURL"
                                    label="*Key Server URL"
                                    hint="e.g. https://test.playready.microsoft.com/service/rightsmanager.asmx"
                                    clearable></v-text-field>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-md-5 p-r-20">
                                  <v-text-field
                                    v-model.trim="output.playready.kidUUID"
                                    :counter="36"
                                    :rules="rules.prKeyID"
                                    label="*UUID representation of Key ID (36 characters)"
                                    hint="e.g. 10000000-1000-1000-1000-100000000001"
                                    clearable></v-text-field>
                                </div>
                                <div class="col-md-7">
                                  <v-text-field
                                    v-model.trim="output.playready.keySeed"
                                    :counter="512"
                                    :rules="rules.prKeySeed"
                                    label="*KeySeed"
                                    clearable></v-text-field>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-md-5 p-r-20">
                                  <v-text-field
                                    v-model.trim="output.playready.cekB64"
                                    :counter="24"
                                    :rules="rules.prCekB64"
                                    label="*Base64 of Content Encryption Key (24 characters)"
                                    clearable></v-text-field>
                                </div>
                              </div>
                            </div>
                          </v-expand-transition>
                        </div>
                      </div>
                    </v-tab-item>
                    <!-- Compress tab -->
                    <v-tab-item>
                      <div class="row p-t-20">
                        <div class="col-md-12 p-r-15">
                          <p class="tab-description p-b-10">
                           {{ text('compress_text') }}
                          </p>
                          <v-text-field
                            :label="text('archive_label')"
                            v-model.trim="output.zip"
                            :placeholder="'Output_' + (i+1) + '.zip'"
                            clearable></v-text-field>
                        </div>
                      </div>
                    </v-tab-item>
                  </v-tabs>
                </div>
              </div>
            </div>
            <!-- Add output button -->
            <div class="row p-l-45 add-output">
              <i class="zmdi zmdi-plus p-r-5"></i>
              <a @click="addOutput()">{{ text('add_output_btn') }}</a>
            </div>
            <!-- Action button -->
            <div class="row text-right p-t-10">
              <div class="col-md-2 col-l"></div>
              <div class="col-md-10">
                <v-btn
                  large
                  :loading="isSaving"
                  :disabled="isSaving || !isJobValid"
                  color="success"
                  @click="saveJob">
                  <span>{{ text('create_job_btn') }}</span>
                </v-btn>
              </div>
            </div>
            <!-- Format Info Dialog -->
            <v-dialog v-model="formatInfoDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
              <v-card>
                <v-toolbar dark color="primary">
                  <v-btn icon dark @click="formatInfoDialog = false">
                    <v-icon>zmdi-close</v-icon>
                  </v-btn>
                  <v-toolbar-title>Video file format description</v-toolbar-title>
                </v-toolbar>
                <table class="format-description">
                  <tr v-for="(format) in formats" class="p-3" :key="format.title">
                    <td>
                      <img :alt='format.title' class="m-2" :src="'/images/video_formats/' + format.extId + '.png'" style="width: 150px;">
                    </td>
                    <td>
                      <h4 class="p-b-10">{{ format.title }}</h4>
                      <p v-html="format.description"></p>
                    </td>
                  </tr>
                </table>
              </v-card>
            </v-dialog>
          </v-card>
        </v-form>
      </div>
    </div>

    <v-dialog v-model="copyDialog" max-width="800" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ text('copy_job_header') }}
        </v-card-title>
        <v-card-title>
          <v-text-field
            v-if="fileJobs.length > 10"
            v-model="search"
            append-icon="search"
            label="Search"
            single-line
            hide-details
            clearable>
          </v-text-field>
        </v-card-title>
        <v-data-table
          :headers="jobHeaders"
          :items="fileJobs"
          :search="search"
          :page.sync="page"
          :pagination.sync="pagination"
          :hide-actions="fileJobs.length < 5"
          :items-per-page="itemsPerPage"
          hide-default-footer
          class="elevation-1">
          <template v-slot:items="props">
            <tr @click="selectJob(props.item)">
              <td class="pointer">{{ props.item.name }}</td>
              <td class="pointer">{{ formatCreateTime(props.item.create_time) }}</td>
              <td class="pointer">{{ jobStatus(props.item) }}</td>
            </tr>
          </template>
        </v-data-table>
        <v-card-actions class="p-t-20 p-b-20">
          <v-spacer></v-spacer>
          <v-btn
            depressed
            text
            @click="copyDialog = false">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import { ENCODING_FORMATS, LANG_CODES } from '@/components/transcoding/shared/types';
import { processURLInfo } from '@/components/transcoding/shared/url';

const outputObject = {
  format: 'segmented_hls',
  drm: 'none',
  urls: [], // array of urlObject
  zip: '',
  segmentSize: 4,
  hlsVersion: 3,
  widevine: {
    licenseServer: '',
    provider: '',
    key: '',
    iv: '',
    packageName: '',
  },
  playready: {
    licenseServerURL: '',
    kidUUID: '',
    keySeed: '',
    cekB64: '',
  },
  subtitles: [],
};

const urlObject = {
  url: '',
  selectedType: 'http',
  login: '',
  password: '',
  hostPath: '',
  secure: false, // Only for HTTP and FTP
  showDetails: false,
};

const inputSupportedSchemes = {
  http: true,
  https: true,
  ftp: true,
  sftp: true,
  aspera: true,
  azure: true,
};

const outputSupportedSchemes = {
  ftp: true,
  sftp: true,
  aspera: true,
  azure: true,
  origin: true,
};

function buildURL(urlObj) {
  let scheme = urlObj.selectedType;
  if (scheme === 'http' && urlObj.secure) {
    scheme = 'https';
  }
  if (scheme === 'ftp' && urlObj.secure) {
    scheme = 'sftp';
  }
  let lp = '';
  if (urlObj.login) {
    lp = urlObj.password ? `${urlObj.login}:${urlObj.password}@` : `${urlObj.login}@`;
  }
  const { hostPath } = urlObj;
  return `${scheme}://${lp}${hostPath}`;
}

function NormOutputToJsonAPI(output) {
  const convertedOutput = {
    format: output.format,
    url: [],
    zip: output.zip,
    map: [],
  };
  if (output.format === 'segmented_hls') {
    convertedOutput.hls_version = Number(output.hlsVersion);
    if (convertedOutput.hls_version === 5) {
      for (let i = 0; i < output.subtitles.length; i++) {
        const sub = output.subtitles[i];
        if (sub.url && sub.code) {
          if (convertedOutput.map.length === 0) {
            convertedOutput.map.push({});
          }
          const title = sub.title || sub.code;
          convertedOutput.map.push({
            source: `${sub.url}?vtt.language=${sub.code}&vtt.title=${title}`,
            track_type: 'text',
            track_language: sub.code,
          });
        }
      }
    }
  }
  for (let i = 0; i < output.urls.length; i++) {
    convertedOutput.url.push(output.urls[i].url);
  }
  if (output.format.startsWith('segmented')) {
    convertedOutput.segment_size = Number(output.segmentSize);
  }
  if (['segmented_dash', 'segmented_ss'].includes(output.format) && output.drm === 'playready') {
    convertedOutput.drm = 'playready';
    convertedOutput.playready_la_url = output.playready.licenseServerURL;
    convertedOutput.playready_key_id = output.playready.kidUUID;
    convertedOutput.playready_key_seed = output.playready.keySeed;
    convertedOutput.playready_content_key = output.playready.cekB64;
  }

  if (output.format === 'segmented_dash' && output.drm === 'widevine') {
    convertedOutput.drm = 'widevine';
    convertedOutput.widevine_license_server = output.widevine.licenseServer;
    convertedOutput.widevine_provider = output.widevine.provider;
    convertedOutput.widevine_key = output.widevine.key;
    convertedOutput.widevine_iv = output.widevine.iv;
    convertedOutput.widevine_package_name = output.widevine.packageName;
  }
  return convertedOutput;
}

function NormJobToJsonAPI(job) {
  const t = + new Date();
  const convertedJob = {
    ext_id: `${t}:${job.name}`,
    type: 'file',
    name: job.name,
    input: {
      url: job.input.url.url,
    },
    outputs: [],
    profile: {
      id: Number(job.profile),
    },
    priority: job.urgentTask ? -1000 : 0,
  };
  for (let i = 0; i < job.outputs.length; i++) {
    convertedJob.outputs.push(NormOutputToJsonAPI(job.outputs[i]));
  }
  return convertedJob;
}

function JsonAPIOutputToNorm(output) {
  const convertedOutput = {
    id: output.id,
    format: output.format,
    urls: [],
    type: '',
    zip: output.zip,
    segmentSize: Number(output.segment_size) || 4,
    hlsVersion: Number(output.hls_version) || 3,
    widevine: {
      licenseServer: '',
      provider: '',
      key: '',
      iv: '',
      packageName: '',
    },
    playready: {
      licenseServerURL: '',
      kidUUID: '',
      keySeed: '',
      cekB64: '',
    },
    subtitles: [],
  };
  for (let i = 0; i < output.url.length; i += 1) {
    if (output.url[i].startsWith('origin')) {
      convertedOutput.urls.push(Vue.util.extend({}, urlObject));
      convertedOutput.urls[i].selectedType = 'origin';
      convertedOutput.urls[i].url = 'origin://';
    } else {
      const newURLObj = processURLInfo({ url: output.url[i] }, outputSupportedSchemes, 'http');
      convertedOutput.urls.push(Vue.util.extend({}, newURLObj));
    }
  }
  return convertedOutput;
}

function JsonAPIJobToNorm(job) {
  const convertedJob = {
    id: job.id,
    name: job.name,
    input: {},
    profile: Number(job.profile.id),
    outputs: [],
  };
  // input
  const newURLObj = processURLInfo({ url: job.input.url }, inputSupportedSchemes);
  convertedJob.input.url = Vue.util.extend({}, newURLObj);

  // output
  for (let i = 0; i < job.outputs.length; i += 1) {
    convertedJob.outputs.push(JsonAPIOutputToNorm(job.outputs[i]));
  }
  return convertedJob;
}

export default {
  created() {
    this.form.input.url = Vue.util.extend({}, urlObject);

    const output = Vue.util.extend({}, outputObject);
    output.urls = [];
    const urlObj = Vue.util.extend({}, urlObject);
    urlObj.selectedType = 'ftp';
    output.urls.push(urlObj);
    output.subtitles = [];
    output.subtitles.push(Vue.util.extend({ code: '', title: '' }, urlObject));
    this.form.outputs.push(output);

    for (const code in LANG_CODES) {
      this.langCodes.push({ code, title: LANG_CODES[code] });
    }

    this.getJobs();

    this.$store.commit('setPageLoader');
    this.$store.dispatch('encodingProfiles/getProfiles')
      .then((profiles) => {
        this.profiles = profiles;
      })
      .catch((errorMsg) => {
        this.$toasted.error(errorMsg, {
          theme: 'toasted-primary',
          position: 'top-center',
          duration: 10000,
        });
      })
      .finally(() => this.$store.commit('resolvePageLoader'));
  },
  data() {
    return {
      isFormValid: false,
      isSaving: false,
      currentTab: 'general',
      formatInfoDialog: false,
      form: {
        name: '',
        profile: '',
        urgentTask: false,
        input: {
          url: {}, // urlObject
        },
        outputs: [], // array of outputObject
      },
      rules: {
        nameRules: [
          v => !!v || this.text('title_req_err'),
          v => (v.length >= 3 && v.length <= 255) || this.text('title_min_err'),
        ],
        segmentSize: [
          v => (v >= 1 && v <= 60) || this.text('segment_size_err'),
        ],
        drmServerURL: [
          v => (!v || v.startsWith('https://')) || '',
        ],
        wvProvider: [
          v => (!v || (v.length >= 1 && v.length <= 512)) || 'Invalid provider',
        ],
        wvKey: [
          v => (!v || v.length === 64) || '',
        ],
        wvIV: [
          v => (!v || v.length === 32) || '',
        ],
        wvPackageName: [
          v => (!v || (v.length >= 3 && v.length <= 128)) || 'Package name must be between 3 and 128 characters',
        ],
        prKeyID: [
          v => (!v || v.length === 36) || '',
        ],
        prKeySeed: [
          v => (!v || v.length > 1) || '',
        ],
        prCekB64: [
          v => (!v || v.length === 24) || '',
        ],
      },
      formats: ENCODING_FORMATS,
      profiles: [],
      langCodes: [],
      hlsVersions: [3, 5],
      jobHeaders: [
        {
          text: 'Name',
          value: 'name',
          align: 'left',
        },
        {
          text: 'Created',
          value: 'create_time',
          align: 'left',
        },
        {
          text: 'State',
          value: 'state',
          align: 'left',
        },
      ],
      copyDialog: false,
      page: 1,
      itemsPerPage: 10,
      search: '',
      fileJobs: [],
      pagination: {
        descending: true,
        sortBy: 'create_time',
      },
    };
  },
  computed: {
    loading() {
      return this.$store.getters.isDataLoaded;
    },
    isJobValid() {
      if (!(this.form.name.length >= 3 && this.form.name.length <= 255) || this.form.profile === '') {
        return false;
      }
      if (this.form.input.url.hostPath === '') return false;
      let noOutput = true;
      for (let i = 0; i < this.form.outputs.length; i += 1) {
        const output = this.form.outputs[i];
        if (output.format.startsWith('segmented') && !(output.segmentSize >= 1 && output.segmentSize <= 60)) {
          return false;
        }
        for (let j = 0; j < output.urls.length; j++) {
          const url = output.urls[j];
          if (url.url !== '' && url.selectedType !== 'origin' && url.hostPath === '' ) {
            return false;
          }
          if (url.url !== '') {
            noOutput = false;
          }
        }
      }
      return !noOutput;
    },
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  methods: {
    formatCreateTime(createTime) {
      return moment(createTime).format('YYYY-MM-DD HH:mm');
    },
    jobStatus(job) {
      if (job.input.state === 'pending') {
        return 'Pending';
      }
      if (job.input.state === 'processing') {
        return 'Processing';
      }
      if (job.input.state === 'error') {
        return 'Input Failed';
      }
      if (job.outputs.length) {
        if (job.outputs[0].state === 'pending') {
          return 'Processing';
        }
        if (job.outputs[0].state === 'error') {
          return 'Output Failed';
        }
        if (job.outputs[0].state === 'success') {
          return 'Done';
        }
      }
      return 'Processing';
    },
    getJobs() {
      const args = {
        limit: 300,
        offset: 0,
      };
      this.$store.dispatch('jobs/getFileJobs', args)
        .then((jobs) => {
          this.fileJobs = jobs;
        })
        .catch((errorMsg) => {
          console.log(errorMsg);
        });
    },
    selectJob(job) {
      this.form = JsonAPIJobToNorm(job);
      this.form.name += ' Copy';
      this.copyDialog = false;
    },
    buildInputURL() {
      this.form.input.url.url = buildURL(this.form.input.url);
    },
    onInputURLChange() {
      this.form.input.url = Vue.util.extend({}, processURLInfo(this.form.input.url, inputSupportedSchemes));
    },
    changeInputType(type) {
      this.form.input.url.selectedType = type;
      this.buildInputURL();
    },
    changeInputSecure() {
      this.form.input.url.secure = !this.form.input.url.secure;
      this.buildInputURL();
    },
    outputLabel(i, j) {
      if (this.form.outputs[i].urls.length === 1) {
        return `${this.text('destination_label')}*`;
      }
      const order = j + 1;
      return `${this.text('destination_label')} #${order}`;
    },
    addOutput() {
      const output = Vue.util.extend({}, outputObject);
      output.urls = [];
      const urlObj = Vue.util.extend({}, urlObject);
      urlObj.selectedType = 'ftp';
      output.urls.push(urlObj);
      output.subtitles = [];
      output.subtitles.push(Vue.util.extend({ code: '', title: '' }, urlObject));
      this.form.outputs.push(Vue.util.extend({}, output));
    },
    removeOutput(i) {
      this.form.outputs.splice(i, 1);
    },
    onFormatChange(i) {
      this.setDRM(i, 'none');
    },
    buildOutputUrl(i, j) {
      this.form.outputs[i].urls[j].url = buildURL(this.form.outputs[i].urls[j]);
    },
    onOutputURLChange(i, j) {
      const url = processURLInfo(this.form.outputs[i].urls[j], outputSupportedSchemes, 'ftp');
      Vue.set(this.form.outputs[i].urls, j, url);
    },
    changeOutputType(i, j, type) {
      this.form.outputs[i].urls[j].selectedType = type;
      this.buildOutputUrl(i, j);
    },
    changeOutputSecure(i, j) {
      this.form.outputs[i].urls[j].secure = !this.form.outputs[i].urls[j].secure;
      this.buildOutputUrl(i, j);
    },
    addOutputURL(i) {
      const urlObj = Vue.util.extend({}, urlObject);
      urlObj.selectedType = 'ftp';
      this.form.outputs[i].urls.push(urlObj);
    },
    removeOutputURL(i, j) {
      this.form.outputs[i].urls.splice(j, 1);
    },
    setDRM(i, type) {
      this.form.outputs[i].drm = type;
    },
    addSubtitles(i) {
      this.form.outputs[i].subtitles.push(Vue.util.extend({ code: '', title: '' }, urlObject));
    },
    removeSubtitles(i, j) {
      this.form.outputs[i].subtitles.splice(j, 1);
    },
    onSubtitleChange(i, j) {
      Vue.set(this.form.outputs[i].subtitles, j, processURLInfo(this.form.outputs[i].subtitles[j], inputSupportedSchemes));
    },
    saveJob() {
      this.isSaving = true;
      if (!this.isJobValid) return;

      const job = NormJobToJsonAPI(this.form);

      this.$store.dispatch('jobs/createJob', job)
        .then((result) => {
          this.$router.push({
            name: 'FileTranscodingJobDetailPage',
            params: {
              id: result.id,
            },
          });
        })
        .catch((errorMsg) => {
          this.isSaving = false;
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 7000,
          });
        });
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
};
</script>
